import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Brand, Category, Product } from "@/types";

export interface ProductStock {
  total: number;
  minStock: number;
  low: boolean;
  byBranch: Record<string, number>;
}

export interface CatalogData {
  products: Product[];
  categories: Category[];
  brands: Brand[];
  stockByProduct: Record<string, ProductStock>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const PRODUCT_COLUMNS =
  "id, name, sku, barcode, internal_description, catalog_description, category_id, brand_id, model, image_url, cost_price, price, wholesale_price, tax_rate, unit, min_stock, show_in_catalog, offer_active, tire_size, compatibility, notes, active, created_at, updated_at";

export function useCatalogData(): CatalogData {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [stockByProduct, setStockByProduct] = useState<Record<string, ProductStock>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsRes, categoriesRes, brandsRes, inventoryRes] = await Promise.all([
        supabase.from("products").select(PRODUCT_COLUMNS).order("created_at", { ascending: false }),
        supabase.from("product_categories").select("id, name, description, active").order("name"),
        supabase.from("brands").select("id, name, active").order("name"),
        supabase.from("inventory").select("product_id, branch_id, stock, min_stock"),
      ]);

      if (productsRes.error) {
        setError("No se pudieron cargar los productos.");
        setProducts([]);
        return;
      }

      const productList = (productsRes.data ?? []) as Product[];
      const categoryList = (categoriesRes.data ?? []) as Category[];
      const brandList = (brandsRes.data ?? []) as Brand[];
      const categoryMap = new Map(categoryList.map((c) => [c.id, c.name]));
      const brandMap = new Map(brandList.map((b) => [b.id, b.name]));

      const enriched = productList.map((product) => ({
        ...product,
        category_name: product.category_id ? categoryMap.get(product.category_id) ?? null : null,
        brand_name: product.brand_id ? brandMap.get(product.brand_id) ?? null : null,
      }));

      const stockMap: Record<string, ProductStock> = {};
      (inventoryRes.data ?? []).forEach((row) => {
        const productId = row.product_id as string | null;
        if (!productId) return;
        const stock = Number(row.stock ?? 0);
        const minStock = Number(row.min_stock ?? 0);
        const bucket = stockMap[productId] ?? { total: 0, minStock: 0, low: false, byBranch: {} };
        bucket.total += stock;
        bucket.minStock += minStock;
        if (row.branch_id) bucket.byBranch[row.branch_id] = stock;
        stockMap[productId] = bucket;
      });
      Object.keys(stockMap).forEach((productId) => {
        const bucket = stockMap[productId];
        bucket.low = bucket.total <= bucket.minStock;
      });

      setProducts(enriched);
      setCategories(categoryList);
      setBrands(brandList);
      setStockByProduct(stockMap);
    } catch {
      setError("No se pudieron cargar los productos.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { products, categories, brands, stockByProduct, loading, error, reload: load };
}